<template>
  <div class="product-member-area-reply-comment px-3">
    <div class="d-flex align-items-center mb-5">
      <div class="flex-fill">
        <span class="lesson-name mr-3" v-if="comment.lesson_name">
          #{{ comment.lesson_name }}
        </span>
        <span class="module-name" v-if="comment.module_name">
          #{{ comment.module_name }}
        </span>
      </div>
      <button
        v-if="!loading && !(disableButton && !comment.parent_id)"
        class="btn btn-sm"
        @click="$emit('delete', comment)"
      >
        <i class="fa-regular fa-trash-can text-danger" />
      </button>
      <button class="btn btn-sm" @click="$emit('close')">
        <i class="fa fa-close fs-20 text-neutral-300" />
      </button>
    </div>

    <div v-if="loading" class="d-flex justify-content-center my-9">
      <b-spinner variant="primary" />
    </div>

    <div v-else>
      <comment :comment="comment" />

      <div class="d-flex justify-content-end my-3 icons">
        <div
          class="d-flex align-items-center fs-12 mr-2"
          :class="comment.likes > 0 ? 'text-primary' : 'text-neutral-500'"
          @click.stop="like"
        >
          {{ comment.likes }}
          <i
            class="fa-heart fs-14 ml-2"
            :class="comment.likes ? 'fa' : 'fa-regular'"
          />
        </div>
        <div
          class="d-flex align-items-center fs-12"
          :class="
            comment.num_children > 0 ? 'text-primary' : 'text-neutral-500'
          "
        >
          {{ comment.num_children }}
          <i
            class="fa-message fs-14 ml-2"
            :class="comment.num_children ? 'fa' : 'fa-regular'"
          />
        </div>
      </div>

      <div
        v-if="disableButton && !comment.parent_id"
        class="row align-items-center my-6 my-lg-5"
      >
        <div
          v-if="comment.num_children"
          class="col d-flex order-1 text-primary mb-4 mb-lg-0"
        >
          <bp-icon :size="16" icon="fa-regular fa-info-circle" class="mr-3" />
          <span>
            Aprove o comentário principal para manipular respostas.
          </span>
        </div>
        <div v-else class="flex-fill" />
        <div
          class="col-lg-2 order-2 order-lg-3 pl-lg-2 mb-3 my-5 my-lg-0 mb-lg-0"
        >
          <button
            class="btn btn-md btn-new-outline-success btn-block"
            @click="publish(comment.id)"
          >
            Aprovar
          </button>
        </div>
        <div class="col-lg-2 order-3 order-lg-2 pr-lg-2">
          <button
            class="btn btn-md btn-new-outline-danger btn-block"
            @click="$emit('delete', comment)"
          >
            Excluir
          </button>
        </div>
      </div>

      <div class="card" v-if="comment.children && comment.children.length">
        <div class="edit-buttons">
          <b-checkbox
            class="ml-2"
            v-model="selectAll"
            @change="setChecked(selectAll)"
            :disabled="disableSelectAll"
          >
            <div class="text-neutral-700 w-100 fs-14 mt-1">
              Selecionar todos
            </div>
          </b-checkbox>
          <div class="d-flex">
            <button
              class="btn btn-sm"
              :disabled="!checkedChildren.length || disableButton"
              @click="updateMany('published')"
            >
              <i class="fa fa-check text-positive-500" />
            </button>
            <button
              class="btn btn-sm"
              :disabled="!checkedChildren.length || disableButton"
              @click="updateMany('blocked')"
            >
              <i class="fa fa-eye-slash fw-9 text-neutral-700" />
            </button>
            <button
              class="btn btn-sm"
              :disabled="!checkedChildren.length || disableButton"
              @click="deleteMany"
            >
              <i class="fa-regular fa-trash-can text-danger" />
            </button>
          </div>
        </div>

        <div class="p-3" :class="{ 'editing-comment': commentToEdit }">
          <div
            v-for="child in comment.children.toReversed()"
            class="mb-5"
            :key="child.id"
            :class="{ 'comment-to-edit': commentToEdit === child.id }"
          >
            <div v-if="child.is_producer">
              <div class="reply-date pb-2">
                {{ timeFromNow(child.created_at) }}
              </div>
              <div class="d-flex justify-content-end align-items-center">
                <button
                  v-if="commentToEdit === child.id"
                  class="btn btn-sm p-0 mr-7"
                  @click="clearEdit"
                >
                  <i class="fa fa-close fs-18 text-neutral-300" />
                </button>

                <div v-else>
                  <button
                    class="btn btn-sm p-0 mx-3"
                    @click="editComment(child)"
                    :disabled="disableButton"
                  >
                    <i class="fa-regular fa-pen fw-9 text-primary" />
                  </button>
                  <button
                    class="btn btn-sm p-0 mx-3"
                    @click="removeChild(child)"
                    :disabled="disableButton"
                  >
                    <i class="fa-regular fa-trash-can text-danger" />
                  </button>
                </div>
                <div class="reply">
                  {{ child.text }}
                </div>
              </div>
            </div>

            <div v-else class="d-flex align-items-center">
              <b-checkbox v-model="child.checked" :disabled="disableButton" />
              <comment :comment="child" :class="child.status" class="mr-3" />
              <div class="d-flex d-lg-block">
                <button
                  class="btn btn-sm p-0 mx-3"
                  :disabled="disableButton"
                  @click="
                    changeStatus(
                      child.id,
                      isPublished(child) ? 'blocked' : 'published'
                    )
                  "
                >
                  <i
                    class="fa fw-9"
                    :class="
                      isPublished(child)
                        ? 'fa-eye-slash text-neutral-700'
                        : 'fa-check text-positive-500'
                    "
                  />
                </button>
                <button
                  class="btn btn-sm p-0 ml-3"
                  @click="removeChild(child)"
                  :disabled="disableButton"
                >
                  <i class="fa-regular fa-trash-can text-danger" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-control mt-5">
        <textarea
          @input="resizeInput()"
          v-model="form.text"
          type="textarea"
          ref="textInput"
          :rows="1"
          placeholder="Escreva sua resposta"
        />
        <button
          class="btn btn-40 btn-square"
          :disabled="!form.text || buttonLoading"
          @click="submit"
        >
          <b-spinner v-if="buttonLoading" variant="primary" />
          <i class="fa fa-paper-plane" v-else />
        </button>
      </div>

      <confirmation
        v-model="confirmation"
        new-design
        confirm-button-class="btn-danger"
        text="Excluir comentário?"
        @confirm="remove"
      />
    </div>
  </div>
</template>

<script>
import {
  deleteArrayItem,
  formFields,
  timeFromNow,
  updateArrayItem
} from "@/functions/helpers.js";
import Comment from "./Comment";

export default {
  props: {
    mainComment: Object
  },
  components: { Comment },
  data() {
    return {
      selectAll: false,
      loading: false,
      buttonLoading: false,
      comment: this.mainComment,
      ...formFields(["text"]),
      confirmation: false,
      itemToDelete: {},
      commentToEdit: null
    };
  },
  computed: {
    disableSelectAll() {
      return (
        !this.comment.children.find(c => !c.is_producer) ||
        this.comment.status !== "published"
      );
    },
    disableButton() {
      return this.comment.status !== "published";
    },
    checkedChildren() {
      return (this.comment.children || []).filter(
        c => !c.is_producer && c.checked
      );
    },
    allChecked() {
      return !this.comment.children.find(c => !c.is_producer && !c.checked);
    }
  },
  methods: {
    publish(id) {
      this.$emit("publish", "published", id);
      this.$emit("close");
    },
    resizeInput() {
      if (this.$refs.textInput.scrollHeight < 29) return;
      this.$refs.textInput.style.height = "";
      this.$refs.textInput.style.height =
        this.$refs.textInput.scrollHeight <= 64
          ? this.$refs.textInput.scrollHeight + "px"
          : "65px";
    },
    timeFromNow,
    editComment(comment) {
      this.commentToEdit = comment.id;
      this.form.text = comment.text;
      this.$refs.textInput.focus();
    },
    clearEdit() {
      this.commentToEdit = null;
      this.form.text = null;
    },
    submit() {
      this.commentToEdit ? this.updateReply() : this.postReply();
    },
    updateReply() {
      this.buttonLoading = true;
      this.$store
        .dispatch("membersComments/update", {
          text: this.form.text,
          id: this.commentToEdit
        })
        .then(({ data }) => {
          this.comment.children = updateArrayItem(this.comment.children, data);
          this.clearEdit();
          this.$emit("update");
        })
        .catch(() => {
          this.$message.error("Erro ao atualizar resposta");
        })
        .finally(() => (this.buttonLoading = false));
    },
    postReply() {
      this.buttonLoading = true;
      this.$store
        .dispatch("membersComments/post", {
          read: true,
          text: this.form.text,
          parent_id: this.comment.id,
          lesson_id: this.comment.lesson_id
        })
        .then(({ data }) => {
          this.form.text = "";
          this.$emit("update");
          this.comment.children = [data, ...this.comment.children];
        })
        .catch(() => {
          this.$message.error("Erro ao enviar resposta");
        })
        .finally(() => (this.buttonLoading = false));
    },
    like() {
      this.comment.liked = !this.comment.liked;
      this.comment.likes = this.comment.liked
        ? this.comment.likes + 1
        : this.comment.likes - 1;
      this.$store
        .dispatch("membersComments/like", this.comment.id)
        .catch(() => {
          this.comment.liked = !this.comment.liked;
        });
    },
    isPublished(comment) {
      return comment.status === "published";
    },
    updateMany(status) {
      let childrenToUpdate = this.checkedChildren
        .filter(c => c.status !== status)
        .map(c => c.id);
      this.$store
        .dispatch("membersComments/updateMany", {
          comments: childrenToUpdate.map(c => ({ id: c, status }))
        })
        .then(() => {
          this.comment.children = this.comment.children.map(comment => {
            return childrenToUpdate.includes(comment.id)
              ? { ...comment, status }
              : comment;
          });
          this.$emit("update");
          this.setChecked(false);
          this.$message.success("Comentários atualizados com sucesso");
        })
        .catch(() => this.$message.error("Erro ao atualizar comentários"));
    },
    changeStatus(id, status) {
      this.$store
        .dispatch("membersComments/update", {
          id,
          status,
          read: true
        })
        .then(({ data }) => {
          this.$emit("update");
          this.comment.children = updateArrayItem(this.comment.children, data);
          this.$forceUpdate();
        })
        .catch(error => {
          this.$message.error(
            error.response?.data?.status
              ? error.response.data.status[0]
              : "Erro ao atualizar comentário"
          );
        });
    },
    removeChild(comment) {
      this.itemToDelete = comment;
      this.confirmation = true;
    },
    deleteMany() {
      let comments_ids = this.checkedChildren.map(c => c.id);
      this.$store
        .dispatch("membersComments/deleteMany", {
          comments_ids
        })
        .then(() => {
          this.$emit("update");
          this.setChecked(false);
          this.comment.children = this.comment.children.filter(
            c => !comments_ids.includes(c.id)
          );
          this.$message.success("Comentários apagados com sucesso");
        })
        .catch(() => {
          this.$message.error("Erro ao apagar comentários");
        });
    },
    remove(comment = this.itemToDelete) {
      this.$store
        .dispatch("membersComments/remove", comment.id)
        .then(() => {
          this.$emit("update");
          this.comment.children = deleteArrayItem(
            this.comment.children,
            comment
          );
          this.comment.num_children--;
        })
        .catch(() => this.$message.error("Erro ao apagar comentário"))
        .finally(() => (this.itemToDelete = null));
    },
    setChecked(v) {
      let children = [...this.comment.children];
      if (children.length) {
        this.comment.children = children.map(c =>
          c.is_producer ? c : { ...c, checked: v }
        );
      }
    },
    async dispatch() {
      this.loading = true;
      if (this.mainComment.parent_id) {
        await this.$store
          .dispatch("membersComments/getComment", this.mainComment.parent_id)
          .then(comment => (this.comment = comment))
          .catch(error => {
            this.comment = this.mainComment;
          });
      }
      this.loading = false;
    }
  },
  watch: {
    allChecked(val) {
      this.selectAll = val;
    }
  },
  mounted() {
    this.dispatch();
    this.setChecked(false);
  }
};
</script>

<style lang="scss" scoped>
.product-member-area-reply-comment {
  ::v-deep {
    .custom-checkbox:has(input:disabled) {
      opacity: 0.4;
    }
  }
  i {
    font-size: 14px;
  }
  button:disabled {
    i {
      color: #666666 !important;
      font-weight: 900 !important;
      opacity: 0.4;
    }
  }
  .lesson-name {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: rgba(119, 120, 142, 0.87);
  }
  .module-name {
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: rgba(155, 157, 176, 0.86);
  }
  ::v-deep {
    .comment {
      background: rgba(255, 255, 255, 0.48);
      box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.08);
    }
  }
  .card {
    border: none;
    min-height: fit-content;
    max-height: 460px;
    overflow: auto;
    position: relative;
    border-radius: 6px;
    background: rgba(224, 224, 227, 0.2);
    .edit-buttons {
      top: 0;
      z-index: 2;
      width: 100%;
      min-height: 40px;
      max-height: 40px;
      display: flex;
      position: sticky;
      align-items: center;
      justify-content: space-between;
      backdrop-filter: blur(16px);
      background: rgba(255, 255, 255, 0.16);
      border-bottom: 1px solid rgba(219, 221, 230, 0.85);
    }
    .editing-comment {
      > * {
        transition: 0.2s all;
        &:not(.comment-to-edit) {
          filter: blur(16px);
        }
      }
    }
    .comment,
    .reply {
      max-width: 70%;
      &.blocked {
        opacity: 0.6;
      }
    }
    .reply-date {
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
      text-align: center;
      color: rgba(119, 120, 142, 0.87);
    }
    .reply {
      padding: 8px;
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      border-radius: 4px;
      background: #fff;
      color: rgba(87, 88, 107, 0.88);
      white-space: break-spaces;
    }
    button {
      height: min-content;
    }
  }

  .form-control {
    display: flex;
    padding-right: 0;
    align-items: center;
    height: unset;
    textarea {
      flex: 1;
      border: 0;
      resize: none;
      &:is(:focus, :active) {
        border: 0;
        outline: 0;
      }
      &::placeholder {
        color: #77788edd;
        font-size: 14px;
      }
    }
    .fa-paper-plane {
      color: #9c5bfb;
    }
    &:focus-within {
      border-color: rgba(196, 157, 255, 0.9);
    }
  }
}
</style>
